import React, { createContext, useContext } from "react";

export const METRIKA_ID = 93997310;

/**
 * Откуда покупка: store — «другие магазины» (/store/...), deals — страницы скидок и каталог,
 * favorites — избранное в кабинете, steam — пополнение Steam на /steam.
 */
export const PURCHASE_SOURCES = ["store", "deals", "favorites", "steam"];

/** Сколько ждать колбэк reachGoal перед уходом на оплату. */
const GOAL_FLUSH_MS = 400;

export function reachGoal(goal, params) {
  try {
    window.ym?.(METRIKA_ID, "reachGoal", goal, params);
  } catch {
    // Метрика заблокирована или не загрузилась — покупке это не мешает.
  }
}

/**
 * reachGoal с колбэком Метрики и таймаутом перед переходом на оплату.
 * @returns {Promise<void>}
 */
export function reachGoalAsync(goal, params) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const timer = window.setTimeout(finish, GOAL_FLUSH_MS);
    try {
      if (typeof window.ym !== "function") {
        window.clearTimeout(timer);
        finish();
        return;
      }
      window.ym(METRIKA_ID, "reachGoal", goal, params, () => {
        window.clearTimeout(timer);
        finish();
      });
    } catch {
      window.clearTimeout(timer);
      finish();
    }
  });
}

const PurchaseSourceContext = createContext(null);

export function usePurchaseSource(fallback) {
  return useContext(PurchaseSourceContext) || fallback;
}

/** Источник для вложенных форм покупки; внешний источник (например, избранное) важнее. */
export function PurchaseSource({ value, children }) {
  const outer = useContext(PurchaseSourceContext);
  return (
    <PurchaseSourceContext.Provider value={outer || value}>{children}</PurchaseSourceContext.Provider>
  );
}

/** Задаёт источник для всего, что внутри, перекрывая внутренние значения по умолчанию. */
export function PurchaseSourceOverride({ value, children }) {
  return <PurchaseSourceContext.Provider value={value}>{children}</PurchaseSourceContext.Provider>;
}

/**
 * Счёт создан: общая цель invoice и invoice_<source>.
 * Обе цели отправляем сразу и ждём их колбэки перед переходом на оплату,
 * не дольше GOAL_FLUSH_MS в сумме. Сумма, номер заказа и итог оплаты в Метрику не уходят.
 * @returns {Promise<void>}
 */
export async function trackInvoice(source, { multi } = {}) {
  const params = { source, ...(multi ? { multi: true } : {}) };
  await Promise.all([
    reachGoalAsync("invoice", params),
    reachGoalAsync(`invoice_${source}`, params),
  ]);
}
